import { ExternalLink, Star } from 'lucide-react';

interface AppCardProps {
  title: string;
  description: string;
  imageUrl: string;
  link?: string;
  accessMessage?: string;
  accessMessageColor?: 'green' | 'red' | 'orange';
  disabled?: boolean;
  services?: string[];
  isPWA?: boolean;
  imagePosition?: string;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export default function AppCard({ title, description, imageUrl, link = '#', accessMessage, accessMessageColor = 'green', disabled = false, services, isPWA = false, imagePosition = 'object-top', isFavorite = false, onToggleFavorite }: AppCardProps) {
  return (
    <div className="group bg-hub-card border border-hub-border rounded-xl overflow-hidden flex flex-col transition-all duration-300 hover:scale-[1.02] hover:border-hub-accent/50 hover:shadow-[0_0_24px_rgba(59,130,246,0.12)] cursor-pointer">
      <div className="relative w-full aspect-video overflow-hidden bg-hub-bg flex items-center justify-center">
        <img
          src={imageUrl}
          alt={title}
          className={`w-full h-full object-cover ${imagePosition} transition-transform duration-500 group-hover:scale-105`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-hub-card/80 via-transparent to-transparent" />

        {onToggleFavorite && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFavorite();
            }}
            aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            className="absolute top-2 right-2 z-10 w-7 h-7 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 transition-colors"
          >
            <Star
              size={14}
              className={isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-white'}
            />
          </button>
        )}

        {services && services.length > 0 && (
          <div className={`absolute top-2 left-2 flex flex-wrap gap-1 ${onToggleFavorite ? 'right-10' : 'right-2'}`}>
            {services.map((service, index) => (
              <span
                key={index}
                className="bg-hub-blue/90 text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wide backdrop-blur-sm"
              >
                {service}
              </span>
            ))}
          </div>
        )}
        
        <div className="absolute bottom-2 right-2">
          {isPWA ? (
            <div className="group/pwa relative">
              <span className="bg-red-600/90 text-white text-[9px] font-bold px-2 py-1 rounded uppercase tracking-wide backdrop-blur-sm">
                PWA
              </span>
              <div className="absolute bottom-full right-0 mb-2 hidden group-hover/pwa:block w-48 bg-gray-900/95 text-white text-[10px] p-2 rounded shadow-lg backdrop-blur-sm">
                <p className="font-semibold mb-1">Application installable</p>
                <p className="text-gray-300">Cette application peut être installée sur votre appareil pour un accès rapide.</p>
              </div>
            </div>
          ) : (
            <span className="bg-gray-500/90 text-white text-[9px] font-bold px-2 py-1 rounded uppercase tracking-wide backdrop-blur-sm">
              WEB
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center text-center px-5 py-5 flex-1 gap-2">
        <h3 className="text-hub-text font-bold text-sm tracking-widest uppercase leading-tight">
          {title}
        </h3>
        <p className="text-hub-muted text-xs leading-relaxed">
          {description}
        </p>

        {accessMessage && (
          <p className={`${accessMessageColor === 'red' ? 'text-red-500' : accessMessageColor === 'orange' ? 'text-orange-500 animate-pulse' : 'text-green-500'} text-xs font-semibold mt-1`}>
            {accessMessage}
          </p>
        )}

        <div className="mt-auto pt-3 w-full flex justify-center">
          {disabled ? (
            <button
              disabled
              className="inline-flex items-center gap-2 bg-gray-400 text-white text-xs font-bold tracking-widest uppercase px-8 py-2 rounded cursor-not-allowed opacity-60"
            >
              <ExternalLink size={11} />
              OUVRIR
            </button>
          ) : (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-hub-blue hover:bg-hub-blue-hover text-white text-xs font-bold tracking-widest uppercase px-8 py-2 rounded transition-colors duration-200 whitespace-nowrap"
            >
              <ExternalLink size={11} />
              OUVRIR
            </a>
          )}
        </div>
      </div>
    </div>
  );
}